import { useTranslation as usePublicI18n } from "react-i18next";
import { publicText, publicTemplate } from "./publicText";
import React from "react";
import { CASE04 } from "./case04Data";
const METRIC_CONFIG = {
  solar: {
    title: "日射量 × 気孔開度",
    xLabel: "日射量",
    digits: 0,
    unit: "",
    slopeDigits: 4,
    correlationKey: "日射量"
  },
  temperature: {
    title: "温度 × 気孔開度",
    xLabel: "温度",
    digits: 1,
    unit: "°C",
    slopeDigits: 4,
    correlationKey: "温度"
  },
  vpd: {
    title: "VPD × 気孔開度",
    xLabel: "VPD",
    digits: 2,
    unit: "kPa",
    slopeDigits: 3,
    correlationKey: "VPD"
  }
};
export function LocalNavigation() {
  usePublicI18n();
  const links = [["question", "問い"], ["dataset", "データセット"], ["workflow", "加工手順"], ["metrics", "4指標"], ["sampling", "測定面"], ["daily", "日周変化"], ["granularity", "粒度比較"], ["environment", "環境推移"], ["relationships", "画像単位"], ["hourly-relationships", "1時間集計"], ["metric-comparison", "指標比較"], ["irrigation", "灌水"], ["table", "統合表"], ["interpretation", "考察"], ["method", "方法"]];
  return <nav className="case04-local-nav" aria-label={publicText("Case Study 04 ページ内ナビゲーション")}>
      <div className="case-container case04-local-nav__inner">
        <span>{publicText("ON THIS PAGE")}</span>
        <div>
          {publicText(links.map(([id, label]) => <a key={id} href={`#${id}`}>{publicText(label)}</a>))}
        </div>
      </div>
    </nav>;
}
function formatSigned(value, digits = 2) {
  return `${value >= 0 ? "+" : ""}${value.toFixed(digits)}`;
}
function PoreGlyph({
  mode = "all",
  aspect = 0.30,
  area = 55
}) {
  usePublicI18n();
  const long = Math.min(72, Math.max(42, 42 + area * 0.30));
  const short = Math.min(38, Math.max(8, long * Math.max(0.12, aspect)));
  const cx = 90;
  const cy = 54;
  return <svg className={`case04-pore-glyph is-${mode}`} viewBox="0 0 180 108" aria-hidden="true">
      <ellipse cx={cx} cy={cy} rx="70" ry="43" className="case04-guard-cell" />
      <ellipse cx={cx} cy={cy} rx={long / 2} ry={short / 2} className="case04-pore-opening" />
      {publicText((mode === "aperture" || mode === "all") && <g className="case04-pore-measure is-aperture">
          <line x1={cx} y1={cy - short / 2} x2={cx} y2={cy + short / 2} />
          <path d={`M${cx - 4},${cy - short / 2 + 5} L${cx},${cy - short / 2} L${cx + 4},${cy - short / 2 + 5}`} />
          <path d={`M${cx - 4},${cy + short / 2 - 5} L${cx},${cy + short / 2} L${cx + 4},${cy + short / 2 - 5}`} />
        </g>)}
      {publicText((mode === "area" || mode === "all") && <ellipse cx={cx} cy={cy} rx={long / 2 + 4} ry={short / 2 + 4} className="case04-area-outline" />)}
      {publicText(mode === "aspect" && <g className="case04-pore-measure is-aspect">
          <line x1={cx - long / 2} y1={cy} x2={cx + long / 2} y2={cy} />
          <line x1={cx} y1={cy - short / 2} x2={cx} y2={cy + short / 2} />
          <text x={cx + long / 2 - 4} y={cy - 6}>{publicText("Long")}</text>
          <text x={cx + 7} y={cy - short / 2 + 10}>{publicText("Short")}</text>
        </g>)}
    </svg>;
}
export function MetricDefinitionGrid() {
  usePublicI18n();
  return <div className="case04-definition-grid">
      {publicText(CASE04.definitions.map(item => <article className={`case04-definition-card is-${item.key}`} key={item.key}>
          <div className="case04-definition-visual">
            {publicText(item.key === "openRate" ? <div className="case04-mini-pore-set" aria-hidden="true">
                {publicText(Array.from({
            length: 12
          }, (_, index) => <i key={index} className={index < 7 ? "is-open" : "is-closed"}><b /></i>))}
              </div> : <PoreGlyph mode={item.key === "poreArea" ? "area" : item.key === "aspectRatio" ? "aspect" : "aperture"} aspect={item.key === "aspectRatio" ? 0.32 : 0.28} area={60} />)}
          </div>
          <span>{publicText(item.question)}</span>
          <h3>{publicText(item.label)}</h3>
          <strong>{publicText(item.unit)}</strong>
          <p>{publicText(item.definition)}</p>
        </article>))}
    </div>;
}
function MiniStomata({
  rate
}) {
  usePublicI18n();
  const openCount = Math.round(rate / 5);
  return <div className="case04-mini-stomata" aria-label={publicTemplate(447, [rate.toFixed(1)])}>
      {publicText(Array.from({
      length: 20
    }, (_, index) => <i key={index} className={index < openCount ? "is-open" : "is-closed"}>
          <b />
        </i>))}
    </div>;
}
export function RepresentativeMetricCards() {
  usePublicI18n();
  return <div className="case04-representative-grid">
      {publicText(CASE04.representative.map(row => <article className={`case04-representative-card is-${row.side}`} key={row.time}>
          <header>
            <div>
              <strong>{publicText(row.time)}</strong>
              <span>{publicText(row.sideLabel)}</span>
            </div>
            <small>{publicText(row.images)}{publicText(" images")}</small>
          </header>

          <section className="case04-representative-open">
            <div>
              <span>{publicText("Open\u7387")}</span>
              <strong>{publicText(row.openRate.toFixed(1))}{publicText("%")}</strong>
              <small>{publicText(row.open.toLocaleString())}{publicText(" / ")}{publicText(row.stomata.toLocaleString())}{publicText(" \u6C17\u5B54")}</small>
            </div>
            <MiniStomata rate={row.openRate} />
          </section>

          <div className="case04-representative-shape">
            <PoreGlyph mode="all" aspect={row.aspectRatio} area={row.poreArea} />
            <div className="case04-shape-values">
              <dl>
                <div>
                  <dt>{publicText("Aperture")}</dt>
                  <dd>{publicText(row.aperture.toFixed(2))} <small>{publicText("\xB5m")}</small></dd>
                  <span>{publicText("IQR ")}{publicText(row.apertureQ1.toFixed(2))}{publicText("\u2013")}{publicText(row.apertureQ3.toFixed(2))}</span>
                </div>
                <div>
                  <dt>{publicText("Pore Area")}</dt>
                  <dd>{publicText(row.poreArea.toFixed(1))} <small>{publicText("\xB5m\xB2")}</small></dd>
                  <span>{publicText("IQR ")}{publicText(row.poreAreaQ1.toFixed(1))}{publicText("\u2013")}{publicText(row.poreAreaQ3.toFixed(1))}</span>
                </div>
                <div>
                  <dt>{publicText("Short / Long")}</dt>
                  <dd>{publicText(row.aspectRatio.toFixed(3))}</dd>
                  <span>{publicText("IQR ")}{publicText(row.aspectRatioQ1.toFixed(3))}{publicText("\u2013")}{publicText(row.aspectRatioQ3.toFixed(3))}</span>
                </div>
              </dl>
            </div>
          </div>
          <footer>{publicText("\u5F62\u72B6\u5024\uFF1A\u753B\u50CF\u5225\u4E2D\u592E\u5024\u306E\u6642\u9593\u5185\u5E73\u5747 / IQR\uFF1A\u753B\u50CF\u5225\u4E2D\u592E\u5024\u306E\u5206\u5E03")}</footer>
        </article>))}
    </div>;
}
export function DataWorkflow() {
  usePublicI18n();
  return <div className="case04-workflow">
      {publicText(CASE04.workflow.map((item, index) => <React.Fragment key={item.step}>
          <article className="case04-workflow-step">
            <span>{publicText(item.step)}</span>
            <small>{publicText(item.label)}</small>
            <strong>{publicText(item.value)}</strong>
            <p>{publicText(item.text)}</p>
          </article>
          {publicText(index < CASE04.workflow.length - 1 && <i className="case04-workflow-arrow">{publicText("\u2192")}</i>)}
        </React.Fragment>))}
    </div>;
}
export function AggregationComparison() {
  usePublicI18n();
  const rows = CASE04.aggregation;
  const width = 920;
  const height = 350;
  const pad = {
    left: 58,
    right: 24,
    top: 25,
    bottom: 52
  };
  const plotWidth = width - pad.left - pad.right;
  const plotHeight = height - pad.top - pad.bottom;
  const yMin = 2;
  const yMax = 7;
  const x = index => pad.left + index * plotWidth / (rows.length - 1);
  const y = value => pad.top + plotHeight - (value - yMin) / (yMax - yMin) * plotHeight;
  const imageLine = rows.map((row, index) => `${x(index)},${y(row.apertureImageMean)}`).join(" ");
  const pooledLine = rows.map((row, index) => `${x(index)},${y(row.aperturePooledMedian)}`).join(" ");
  const noon = rows.find(row => row.time === "12:00");
  return <>
      <div className="case04-granularity-grid">
        <article>
          <span>{publicText("STOMA-LEVEL")}</span>
          <strong>{publicText("\u5168Open\u6C17\u5B54\u3092\u5408\u7B97")}</strong>
          <p>{publicText("\u5206\u5E03\u30FB\u500B\u3005\u306E\u6C17\u5B54\u5DEE\u3092\u898B\u308B\u3002\u6C17\u5B54\u304C\u591A\u304F\u5199\u3063\u305F\u753B\u50CF\u307B\u3069\u96C6\u8A08\u3078\u306E\u91CD\u307F\u304C\u5927\u304D\u304F\u306A\u308A\u307E\u3059\u3002")}</p>
        </article>
        <article>
          <span>{publicText("IMAGE-LEVEL")}</span>
          <strong>{publicText("\u753B\u50CF\u3054\u3068\u306B\u4E2D\u592E\u5024")}</strong>
          <p>{publicText("\u64AE\u5F71\u753B\u50CF\u3092\u89B3\u6E2C\u5358\u4F4D\u3068\u3057\u3066\u6271\u3046\u3002\u8449\u30FB\u8996\u91CE\u3054\u3068\u306E\u3070\u3089\u3064\u304D\u3092\u6B8B\u3057\u307E\u3059\u3002")}</p>
        </article>
        <article>
          <span>{publicText("HOURLY")}</span>
          <strong>{publicText("\u753B\u50CF\u4E2D\u592E\u5024\u3092\u6642\u9593\u5185\u5E73\u5747")}</strong>
          <p>{publicText("\u30CE\u30A4\u30BA\u3092\u6574\u7406\u3057\u3066\u65E5\u5468\u30C8\u30EC\u30F3\u30C9\u3092\u898B\u308B\u3002\u7D30\u304B\u306A\u3070\u3089\u3064\u304D\u306F\u898B\u3048\u306B\u304F\u304F\u306A\u308A\u307E\u3059\u3002")}</p>
        </article>
      </div>

      <div className="case04-aggregation-layout">
        <article className="case04-aggregation-chart">
          <div className="case04-aggregation-head">
            <div>
              <span>{publicText("AGGREGATION COMPARISON")}</span>
              <h3>{publicText("Aperture\u306E1\u6642\u9593\u96C6\u8A08\u65B9\u6CD5\u3092\u6BD4\u8F03")}</h3>
            </div>
            <div className="case04-aggregation-legend">
              <span><i className="is-image" />{publicText("\u753B\u50CF\u4E2D\u592E\u5024 \u2192 \u6642\u9593\u5185\u5E73\u5747")}</span>
              <span><i className="is-pooled" />{publicText("\u5168Open\u6C17\u5B54 \u2192 \u6642\u9593\u5185\u4E2D\u592E\u5024")}</span>
            </div>
          </div>
          <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label={publicText("気孔開度の2つの1時間集計方法の比較")}>
            {publicText([2, 3, 4, 5, 6, 7].map(tick => <g key={tick}>
                <line x1={pad.left} y1={y(tick)} x2={width - pad.right} y2={y(tick)} className="case04-scatter-guide" />
                <text x={pad.left - 10} y={y(tick) + 4} textAnchor="end">{publicText(tick)}</text>
              </g>))}
            <polyline points={imageLine} className="case04-aggregation-line is-image" />
            <polyline points={pooledLine} className="case04-aggregation-line is-pooled" />
            {publicText(rows.map((row, index) => <g key={row.time}>
                <circle cx={x(index)} cy={y(row.apertureImageMean)} r="5" className="case04-aggregation-point is-image" />
                <circle cx={x(index)} cy={y(row.aperturePooledMedian)} r="4" className="case04-aggregation-point is-pooled" />
                <text x={x(index)} y={height - 18} textAnchor="middle">{publicText(row.time.slice(0, 2))}</text>
              </g>))}
            <text transform={`translate(17 ${pad.top + plotHeight / 2}) rotate(-90)`} textAnchor="middle">{publicText("Aperture\uFF08\xB5m\uFF09")}</text>
          </svg>
        </article>

        <article className="case04-noon-comparison">
          <span>{publicText("12:00 EXAMPLE")}</span>
          <h3>{publicText("\u96C6\u8A08\u65B9\u6CD5\u3067\u5DEE\u304C\u51FA\u308B\u6642\u9593\u5E2F\u3082\u3042\u308B\u3002")}</h3>
          <dl>
            <div><dt>{publicText("Aperture")}</dt><dd>{publicText(noon.apertureImageMean.toFixed(2))} <i>{publicText("vs")}</i> {publicText(noon.aperturePooledMedian.toFixed(2))}{publicText(" \xB5m")}</dd></div>
            <div><dt>{publicText("Pore Area")}</dt><dd>{publicText(noon.areaImageMean.toFixed(1))} <i>{publicText("vs")}</i> {publicText(noon.areaPooledMedian.toFixed(1))}{publicText(" \xB5m\xB2")}</dd></div>
            <div><dt>{publicText("Short / Long")}</dt><dd>{publicText(noon.aspectImageMean.toFixed(3))} <i>{publicText("vs")}</i> {publicText(noon.aspectPooledMedian.toFixed(3))}</dd></div>
          </dl>
          <p>{publicText("\u5DE6\u304C\u300C\u753B\u50CF\u4E2D\u592E\u5024\u2192\u5E73\u5747\u300D\u3001\u53F3\u304C\u300C\u5168\u6C17\u5B54\u2192\u4E2D\u592E\u5024\u300D\u3002\u3069\u3061\u3089\u304C\u6B63\u3057\u3044\u3068\u3044\u3046\u3088\u308A\u3001\u89E3\u6790\u306E\u554F\u3044\u306B\u5408\u308F\u305B\u3066\u9078\u3073\u307E\u3059\u3002")}</p>
        </article>
      </div>
    </>;
}
export function ScatterPlot({
  metric
}) {
  usePublicI18n();
  const config = METRIC_CONFIG[metric];
  const points = CASE04.scatter.filter(point => point.side !== "switch");
  const regression = CASE04.regressions[metric];
  const width = 640;
  const height = 440;
  const pad = {
    left: 66,
    right: 24,
    top: 32,
    bottom: 64
  };
  const plotWidth = width - pad.left - pad.right;
  const plotHeight = height - pad.top - pad.bottom;
  const values = points.map(point => point[metric]);
  const rawMin = Math.min(...values);
  const rawMax = Math.max(...values);
  const range = rawMax - rawMin || 1;
  const min = metric === "solar" ? 0 : rawMin - range * 0.06;
  const max = rawMax + range * 0.06;
  const yMin = 0;
  const yMax = 8;
  const x = value => pad.left + (value - min) / (max - min) * plotWidth;
  const y = value => pad.top + plotHeight - (value - yMin) / (yMax - yMin) * plotHeight;
  const xTicks = Array.from({
    length: 6
  }, (_, index) => min + (max - min) * index / 5);
  const yTicks = [0, 2, 4, 6, 8];
  const line = side => {
    const fit = regression[side];
    const groupPoints = points.filter(point => point.side === side);
    const x1 = Math.min(...groupPoints.map(point => point[metric]));
    const x2 = Math.max(...groupPoints.map(point => point[metric]));
    return {
      x1: x(x1),
      y1: y(fit.slope * x1 + fit.intercept),
      x2: x(x2),
      y2: y(fit.slope * x2 + fit.intercept)
    };
  };
  const eastLine = line("east");
  const westLine = line("west");
  const corr = CASE04.correlations[config.correlationKey];
  return <article className="case04-scatter-card">
      <div className="case04-scatter-card__head">
        <div><span>{publicText("IMAGE-LEVEL SCATTER")}</span><h3>{publicText(config.title)}</h3></div>
        <div className="case04-scatter-legend">
          <span><i className="is-east" />{publicText("\u5348\u524D\u30FB\u6A39\u6771\u9762")}</span>
          <span><i className="is-west" />{publicText("\u5348\u5F8C\u30FB\u6A39\u897F\u9762")}</span>
        </div>
      </div>
      <svg className="case04-scatter-svg" viewBox={`0 0 ${width} ${height}`} role="img" aria-label={publicTemplate(464, [config.title])}>
        {publicText(yTicks.map(tick => <g key={`y-${tick}`}><line x1={pad.left} y1={y(tick)} x2={width - pad.right} y2={y(tick)} className="case04-scatter-guide" /><text x={pad.left - 12} y={y(tick) + 4} textAnchor="end">{publicText(tick)}</text></g>))}
        {publicText(xTicks.map((tick, index) => <g key={`x-${index}`}><line x1={x(tick)} y1={pad.top} x2={x(tick)} y2={pad.top + plotHeight} className="case04-scatter-guide is-vertical" /><text x={x(tick)} y={height - 34} textAnchor="middle">{publicText(tick.toFixed(config.digits))}</text></g>))}
        {publicText(points.map((point, index) => <circle key={`${metric}-${point.time}-${index}`} cx={x(point[metric])} cy={y(point.aperture)} r="3.6" className={`case04-scatter-point is-${point.side}`}>
            <title>{publicText(`${point.time}｜${config.xLabel} ${point[metric].toFixed(config.digits)}${config.unit}｜Aperture ${point.aperture.toFixed(2)} µm`)}</title>
          </circle>))}
        <line {...eastLine} className="case04-regression-line is-east" />
        <line {...westLine} className="case04-regression-line is-west" />
        <text transform={`translate(18 ${pad.top + plotHeight / 2}) rotate(-90)`} textAnchor="middle" className="case04-axis-title">{publicText("Aperture\uFF08\xB5m\uFF09")}</text>
        <text x={pad.left + plotWidth / 2} y={height - 9} textAnchor="middle" className="case04-axis-title">{publicText(config.xLabel)}{publicText(config.unit ? `（${config.unit}）` : "")}</text>
      </svg>
      <div className="case04-scatter-card__stats">
        <div><span>{publicText("\u5348\u524D\u30FB\u6A39\u6771\u9762")}</span><strong>{publicText("r = ")}{publicText(formatSigned(corr.east.r))}</strong><small>{publicText("R\xB2 = ")}{publicText(regression.east.r2.toFixed(2))}{publicText(" / n = ")}{publicText(regression.east.n)}</small></div>
        <div><span>{publicText("\u5348\u5F8C\u30FB\u6A39\u897F\u9762")}</span><strong>{publicText("r = ")}{publicText(formatSigned(corr.west.r))}</strong><small>{publicText("R\xB2 = ")}{publicText(regression.west.r2.toFixed(2))}{publicText(" / n = ")}{publicText(regression.west.n)}</small></div>
      </div>
    </article>;
}
function equation(fit, slopeDigits) {
  return `y = ${fit.slope.toFixed(slopeDigits)}x ${fit.intercept >= 0 ? "+" : "−"} ${Math.abs(fit.intercept).toFixed(2)}`;
}
export function HourlyScatterPlot({
  metric
}) {
  usePublicI18n();
  const config = METRIC_CONFIG[metric];
  const points = CASE04.rows;
  const fit = CASE04.hourlyRegressions[metric];
  const width = 640;
  const height = 470;
  const pad = {
    left: 66,
    right: 24,
    top: 34,
    bottom: 72
  };
  const plotWidth = width - pad.left - pad.right;
  const plotHeight = height - pad.top - pad.bottom;
  const values = points.map(point => point[metric]);
  const rawMin = Math.min(...values);
  const rawMax = Math.max(...values);
  const range = rawMax - rawMin || 1;
  const min = metric === "solar" ? 0 : rawMin - range * 0.08;
  const max = rawMax + range * 0.08;
  const yMin = 2;
  const yMax = 7;
  const x = value => pad.left + (value - min) / (max - min) * plotWidth;
  const y = value => pad.top + plotHeight - (value - yMin) / (yMax - yMin) * plotHeight;
  const xTicks = Array.from({
    length: 5
  }, (_, index) => min + (max - min) * index / 4);
  const yTicks = [2, 3, 4, 5, 6, 7];
  const line = group => {
    const groupPoints = group === "all" ? points.filter(point => point.side !== "switch") : points.filter(point => point.side === group);
    const x1 = Math.min(...groupPoints.map(point => point[metric]));
    const x2 = Math.max(...groupPoints.map(point => point[metric]));
    return {
      x1: x(x1),
      y1: y(fit[group].slope * x1 + fit[group].intercept),
      x2: x(x2),
      y2: y(fit[group].slope * x2 + fit[group].intercept)
    };
  };
  return <article className="case04-hourly-card">
      <div className="case04-hourly-card__head">
        <div><span>{publicText("HOURLY SUMMARY / 3 FITS")}</span><h3>{publicText(config.title)}</h3></div>
        <div className="case04-hourly-legend">
          <span><i className="is-all" />{publicText("\u5168\u4F53")}</span>
          <span><i className="is-east" />{publicText("\u5348\u524D\u30FB\u6A39\u6771\u9762")}</span>
          <span><i className="is-west" />{publicText("\u5348\u5F8C\u30FB\u6A39\u897F\u9762")}</span>
        </div>
      </div>
      <svg className="case04-hourly-svg" viewBox={`0 0 ${width} ${height}`} role="img" aria-label={publicTemplate(466, [config.title])}>
        {publicText(yTicks.map(tick => <g key={tick}><line x1={pad.left} y1={y(tick)} x2={width - pad.right} y2={y(tick)} className="case04-scatter-guide" /><text x={pad.left - 10} y={y(tick) + 4} textAnchor="end">{publicText(tick)}</text></g>))}
        {publicText(xTicks.map((tick, index) => <g key={index}><line x1={x(tick)} y1={pad.top} x2={x(tick)} y2={pad.top + plotHeight} className="case04-scatter-guide is-vertical" /><text x={x(tick)} y={height - 42} textAnchor="middle">{publicText(tick.toFixed(config.digits))}</text></g>))}
        <line {...line("all")} className="case04-hourly-fit is-all" />
        <line {...line("east")} className="case04-hourly-fit is-east" />
        <line {...line("west")} className="case04-hourly-fit is-west" />
        {publicText(points.map(point => <g key={point.time}>
            <circle cx={x(point[metric])} cy={y(point.aperture)} r="7" className={`case04-hourly-point is-${point.side}`}>
              <title>{publicText(`${point.time}｜${config.xLabel} ${point[metric].toFixed(config.digits)}${config.unit}｜Aperture ${point.aperture.toFixed(2)} µm`)}</title>
            </circle>
            <text x={x(point[metric]) + 8} y={y(point.aperture) - 8} className="case04-hour-label">{publicText(point.time.slice(0, 2))}</text>
          </g>))}
        <text transform={`translate(18 ${pad.top + plotHeight / 2}) rotate(-90)`} textAnchor="middle" className="case04-axis-title">{publicText("Aperture\uFF08\xB5m\uFF09")}</text>
        <text x={pad.left + plotWidth / 2} y={height - 10} textAnchor="middle" className="case04-axis-title">{publicText(config.xLabel)}{publicText(config.unit ? `（${config.unit}）` : "")}</text>
      </svg>
      <div className="case04-equation-grid">
        {publicText(["all", "east", "west"].map(group => <div key={group}>
            <span>{publicText(group === "all" ? "全体" : group === "east" ? "午前・樹東面" : "午後・樹西面")}</span>
            <strong>{publicText(equation(fit[group], config.slopeDigits))}</strong>
            <small>{publicText("R\xB2 = ")}{publicText(fit[group].r2.toFixed(2))}{publicText(" / n = ")}{publicText(fit[group].n)}</small>
          </div>))}
      </div>
      <footer>{publicText("12\u6642\u53F0\u306F\u5207\u66FF\u6642\u9593\u5E2F\u3068\u3057\u3066\u70B9\u306E\u307F\u8868\u793A\u3057\u30013\u672C\u306E\u8FD1\u4F3C\u5F0F\u304B\u3089\u9664\u5916\u3002")}</footer>
    </article>;
}
export function CorrelationCards() {
  usePublicI18n();
  const metrics = ["日射量", "温度", "VPD", "湿度", "CO₂"];
  return <div className="case04-correlation-grid">
      {publicText(metrics.map(metric => {
      const row = CASE04.correlations[metric];
      return <article className="case04-correlation-card" key={metric}>
          <div className="case04-correlation-card__head"><strong>{publicText(metric)}</strong><span>{publicText("\u5168\u4F53 r = ")}{publicText(formatSigned(row.all.r))}</span></div>
          <div className="case04-correlation-bar"><small>{publicText("\u5348\u524D")}</small><i><b className={row.east.r < 0 ? "is-negative" : ""} style={{
              width: `${Math.abs(row.east.r) * 100}%`
            }} /></i><em>{publicText(formatSigned(row.east.r))}</em></div>
          <div className="case04-correlation-bar"><small>{publicText("\u5348\u5F8C")}</small><i><b className={row.west.r < 0 ? "is-negative" : ""} style={{
              width: `${Math.abs(row.west.r) * 100}%`
            }} /></i><em>{publicText(formatSigned(row.west.r))}</em></div>
        </article>;
    }))}
    </div>;
}
export function MetricComparisonMatrix() {
  usePublicI18n();
  const responseOrder = ["aperture", "poreArea", "aspectRatio"];
  const metricOrder = ["solar", "temperature", "vpd"];
  return <div className="case04-matrix">
      <div className="case04-matrix-row is-head">
        <div>{publicText("Response metric")}</div>
        <div>{publicText("\u65E5\u5C04\u91CF")}</div><div>{publicText("\u6E29\u5EA6")}</div><div>{publicText("VPD")}</div>
      </div>
      {publicText(responseOrder.map(response => {
      const row = CASE04.metricComparison[response];
      return <div className="case04-matrix-row" key={response}>
          <div><strong>{publicText(row.label)}</strong><small>{publicText("R\xB2\uFF1A\u753B\u50CF\u5358\u4F4D \u2192 1\u6642\u9593\u5358\u4F4D")}</small></div>
          {publicText(metricOrder.map(metric => {
          const cell = row.metrics[metric];
          return <div className="case04-matrix-cell" key={metric} style={{
            "--strength": cell.hourlyR2
          }}>
              <span>{publicText(cell.imageR2.toFixed(2))}</span><i>{publicText("\u2192")}</i><strong>{publicText(cell.hourlyR2.toFixed(2))}</strong>
              <small>{publicText("n ")}{publicText(cell.imageN)}{publicText(" \u2192 ")}{publicText(cell.hourlyN)}</small>
            </div>;
        }))}
        </div>;
    }))}
    </div>;
}
export function RelatedCases() {
  usePublicI18n();
  const cases = [{
    id: "02",
    path: "/case-studies/02",
    eyebrow: "DRY-DOWN × REWATERING",
    title: "乾くと、気孔応答の違いが見えてきた。",
    text: "水分条件の変化と処理区ごとの気孔応答を追跡。"
  }, {
    id: "03",
    path: "/case-studies/03",
    eyebrow: "HIGH-THROUGHPUT FIELD PHYSIOLOGY",
    title: "商業栽培圃場での大量計測。",
    text: "大量に測れることを示したCase Study 03から、解析編の04へ。"
  }];
  return <div className="case04-related-grid">
    {publicText(cases.map(item => <a href={item.path} className="case04-related-card" key={item.id}><span>{publicText("CASE STUDY ")}{publicText(item.id)}</span><small>{publicText(item.eyebrow)}</small><strong>{publicText(item.title)}</strong><p>{publicText(item.text)}</p><i>{publicText("\u8A73\u3057\u304F\u898B\u308B \u2192")}</i></a>))}
    <a href="/case-studies" className="case04-related-card is-index"><span>{publicText("CASE STUDY INDEX")}</span><small>{publicText("ALL CASE STUDIES")}</small><strong>{publicText("\u3059\u3079\u3066\u306E\u4E8B\u4F8B\u3092\u898B\u308B\u3002")}</strong><p>{publicText("\u51E6\u7406\u3001\u6C34\u5206\u6761\u4EF6\u3001\u5927\u91CF\u8A08\u6E2C\u3001\u30C7\u30FC\u30BF\u89E3\u6790\u307E\u3067\u3002")}</p><i>{publicText("\u4E00\u89A7\u3078 \u2192")}</i></a>
  </div>;
}
